import { fetchNovelChapters } from 'api'
import { useEffect } from 'react'
import { useImmer } from 'use-immer'
import { SchemaNovelChapterShow } from 'api/generated'

interface PageResult {
  total?: number
  current?: number
  pageSize?: number
  [key: string]: any
}

interface NovelChaptersState {
  currentOptions: { novel_id: string | undefined }
  list: SchemaNovelChapterShow[]
  nextPage: number
  hasMore: boolean
  page_result: PageResult
}

export const useAllNovelChapters = (
  novelId: string | number | undefined,
  disabled?: boolean
): SchemaNovelChapterShow[] => {
  const apiParams = {
    // WARN: keep this as a type-only cast; IDs may be int64-shaped strings and must not be coerced in JS.
    novel_id: novelId as string | undefined,
  }

  const [state, updateState] = useImmer<NovelChaptersState>(() => ({
    currentOptions: apiParams,
    list: [],
    nextPage: 1,
    hasMore: true,
    page_result: {},
  }))

  useEffect(() => {
    const fetcherController = new AbortController()

    if (disabled) {
      return
    }

    if (!state.hasMore) {
      return
    }

    const data = {
      signal: fetcherController.signal,
      current: state.nextPage,
      pageSize: 100,
      // WARN: force cast string to number because js do not support int64.
      ...(state.currentOptions as unknown as { novel_id: number }),
    }

    fetchNovelChapters(data).then((response: any) => {
      updateState((draft) => {
        draft.list = draft.list.concat(response?.data)
        draft.hasMore = response?.page_result?.total > response?.page_result?.current * response?.page_result?.pageSize
        draft.nextPage = response?.page_result?.current + 1
        draft.page_result = response.page_result
      })
    })

    return () => {
      fetcherController.abort()
    }
  }, [disabled, state.currentOptions, state.hasMore, state.nextPage, updateState])

  return state.list
}
